/* eslint-disable node/prefer-global/process */
import type { State, StateValue } from '@autobid/nuxt-pinia-store/types/Search'
import type { APIFormatKey } from '../constants/SEARCH_API_FORMAT_TYPES'
import { useSearchStore } from '@autobid/nuxt-pinia-store/store/searchStore/useSearchStore'
import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { FIVE_MINUTES_MS } from '../constants/FIVE_MINUTES_MS'
import { OPEN_APPS_ID } from '../constants/OPEN_APPS_ID'
import { SEARCH_API_FORMAT_TYPES } from '../constants/SEARCH_API_FORMAT_TYPES'
import { debounceAsync } from '../utils/debounceAsync'
import { useUserApps } from './useUserApps'

export function convertValueToApiFormat(key: string, value: StateValue) {
  if (!value) {
    return
  }

  const type = SEARCH_API_FORMAT_TYPES[key as APIFormatKey]

  // todo: replace with dedicated documentation
  if (!type) {
    // console.error(
    //   `
    //    Unrecognized parameter "${key}"!
    //    Search query doesn't accept "${key}" parameter.
    //    Please check your JSON input!
    //    You can check the accepted parameters in the following link:
    //    https://gitlab-p2.autobid.de/services/auctions/blob/develop/docs/GRAPHQL.md
    //    `
    // )
    return
  }

  switch (type) {
    case 'List[int]': {
      if (Array.isArray(value)) {
        return value.map(Number)
      }
      return [Number(value)]
    }
    case 'List[str]': {
      if (Array.isArray(value)) {
        return value.map(String)
      }
      return [String(value)]
    }
    case 'bool': {
      return Boolean(value)
    }
    case 'int': {
      return Number(value)
    }
    case 'str': {
      return value.toString()
    }
  }
}

interface HandleSearchCardParams {
  searchValues: State
  userAppIds: number[]
  justCount?: boolean
}

export function useHandleSearchCard() {
  const { $trpc } = useNuxtApp()

  const handleSearchCard = async ({
    searchValues,
    userAppIds,
    justCount,
  }: HandleSearchCardParams) => {
    delete searchValues.auth

    const filterUserApps = () => {
      const appId = Array.isArray(searchValues.appId)
        ? (searchValues.appId as (string | number)[]).map(Number)
        : [Number(searchValues.appId)]
      const filtered = userAppIds.filter(id => appId.includes(Number(id)))

      if (!filtered.length) {
        delete searchValues.appId
      }
    }

    if (searchValues.appId) {
      filterUserApps()
    }

    const appId = searchValues.appId
      ? searchValues.appId
      : userAppIds ?? OPEN_APPS_ID // default use all user apps or open apps for unauthenticated users

    const result = await $trpc.auctions.getSearchCarsCount.query({
      filters: {
        ...searchValues,
        appId,
      },
      justCount: justCount || false,
    })

    if (justCount) {
      return {
        count: Number(result?.data?.carIds || 0),
      }
    }

    return {
      carIds: result?.data?.carIds ?? [],
    }
  }

  return { handleSearchCard }
}

export function useSearchCarQuery() {
  const { debounced } = storeToRefs(useSearchStore())
  const route = useRoute()
  const { handleSearchCard } = useHandleSearchCard()
  const { userAppIds } = useUserApps()

  // in order to trigger query refetch when query changes (route.query doesn't trigger it)
  const query = computed(() => {
    return route.query
  })

  const debouncedHandleSearch = debounceAsync(handleSearchCard)

  return useQuery({
    queryKey: ['searchResult', query],
    meta: {
      toastOnError: true,
    },
    queryFn: async () => {
      const params = {
        searchValues: route.query as State,
        userAppIds: userAppIds.value,
        justCount: true,
      }
      if (debounced.value) {
        return await debouncedHandleSearch(params)
      }
      return await handleSearchCard(params)
    },
    enabled: process.client,
    refetchInterval: FIVE_MINUTES_MS,
    staleTime: FIVE_MINUTES_MS,
    cacheTime: FIVE_MINUTES_MS,
  })
}
